import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { getSmartSearchTagValues, SmartSearchModel } from 'mbs-ui-kit/smart-search/models/smart-search-model';
import { SmartSearchValidationState } from 'mbs-ui-kit/smart-search/models/smart-search-models';
import { ComputerTagsFacade } from 'mbs-ui-shared@facades/computer.tags.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { QueryPagingParams, QuerySortParams } from 'mbs-ui-shared@models';
import {
  AgentType,
  agentTypeAdapter,
  ComputerBackupRestoreStatus,
  ComputersAPIFilterKeys,
  ComputersHealthFilterType,
  ComputerStatus,
  GetComputersParams,
  OSTypeAdapter
} from 'mbs-ui-shared@models/Computer';
import { ComputersStoreFilters } from 'mbs-ui-shared@models/ComputersStoreFilters';
import { SmartSearchFilterOptions } from 'mbs-ui-shared@models/SmartSearchFilterOptions';
import { SmartSearchTemplateName } from 'mbs-ui-shared@modules/smart-search-templates/models/smart-search-templates-models';
import { SmartSearchUtils } from 'mbs-ui-shared@utils/smartSearchUtils';
import { combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';

const queryValueSeparator = ',';

const filters: SmartSearchFilterOptions[] = [
  { tag: SmartSearchTemplateName.Words, key: ComputersAPIFilterKeys.Search, singleResultValue: true, singleResultSeparator: ' ' },
  {
    tag: SmartSearchTemplateName.BackupStatus,
    key: ComputersAPIFilterKeys.BackupPlanStatuses,
    values: Object.values(ComputerBackupRestoreStatus)
  },
  {
    tag: SmartSearchTemplateName.RestoreStatus,
    key: ComputersAPIFilterKeys.RestorePlanStatuses,
    values: Object.values(ComputerBackupRestoreStatus)
  },
  {
    tag: SmartSearchTemplateName.ProductInstalled,
    key: ComputersAPIFilterKeys.AppIds,
    values: agentTypeAdapter.map((agent) => agent.displayAgentType.toLowerCase()),
    formatValue: (value: string) => {
      return agentTypeAdapter.find((agent) => agent.displayAgentType.toLowerCase() === value.toLowerCase())?.agentType || null;
    }
  },
  {
    tag: SmartSearchTemplateName.ProductNotInstalled,
    key: ComputersAPIFilterKeys.AppIdsNotInstalled,
    values: agentTypeAdapter.map((agent) => agent.displayAgentType.toLowerCase()),
    formatValue: (value: string) => {
      return agentTypeAdapter.find((agent) => agent.displayAgentType.toLowerCase() === value.toLowerCase())?.agentType || null;
    }
  },
  { tag: SmartSearchTemplateName.IP, key: ComputersAPIFilterKeys.IpAddresses },
  { tag: SmartSearchTemplateName.LastLogin, key: ComputersAPIFilterKeys.LastLogin },
  {
    tag: SmartSearchTemplateName.OS,
    key: ComputersAPIFilterKeys.OS,
    values: OSTypeAdapter.map((os) => os.displayOS.toLowerCase()),
    formatValue: (value: string) => {
      return OSTypeAdapter.find((osMap) => osMap.displayOS.toLowerCase() === value.toLowerCase())?.os || null;
    }
  },
  {
    tag: SmartSearchTemplateName.Status,
    key: ComputersAPIFilterKeys.Online,
    values: [ComputerStatus.Online, ComputerStatus.Offline],
    formatValue: (value: string) => {
      return value.toLowerCase() === ComputerStatus.Online ? 'true' : 'false';
    },
    singleResultValue: true
  }
];

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ComputersFiltersWrapper {
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private tagsFacade = inject(ComputerTagsFacade);
  private licenseTypesObservable = toObservable(this.licensesSignalFacade.licenseTypesPrePostByOwner);

  getComputersHttpParams(
    filter: ComputersStoreFilters,
    sorting?: QuerySortParams,
    paging?: QueryPagingParams,
    skipHealthFilters = false
  ): Observable<GetComputersParams> {
    return combineLatest([this.prepareLicenseTypesFilter(filter.searchModel || {}), this.prepareTagsFilter(filter.searchModel || {})]).pipe(
      map(([licenseTypes, tags]) => ({
        ...(skipHealthFilters ? {} : this.prepareHealthFilters(filter.healthType)),
        ...(skipHealthFilters ? {} : { authStatus: filter.authStatus }),
        companyIds: filter.company ? [filter.company] : null,
        licenseTypes: licenseTypes,
        tags,
        loadHidden: filter.hidden || null,
        ...sorting,
        ...paging
      })),
      map((payload) => this.enrichParamsByPayload(this.getSearchHttpParams(filter.searchModel || {}), payload))
    );
  }

  private prepareHealthFilters(healthType: ComputersHealthFilterType): Record<string, any> {
    switch (healthType) {
      case ComputersHealthFilterType.BackupFailed:
        return { healthScopeAppId: AgentType.Backup, healthProblem: true };
      case ComputersHealthFilterType.BackupWarning:
        return { healthScopeAppId: AgentType.Backup, healthWarning: true };
      case ComputersHealthFilterType.BackupSuccess:
        return { [ComputersAPIFilterKeys.BackupPlanStatuses]: ComputerBackupRestoreStatus.Success };
      case ComputersHealthFilterType.BackupOverdue:
        return { [ComputersAPIFilterKeys.BackupPlanStatuses]: ComputerBackupRestoreStatus.Overdue };
      case ComputersHealthFilterType.RMMProblem:
        return { healthScopeAppId: AgentType.RMM, healthProblem: true };
      case ComputersHealthFilterType.RMMWarning:
        return { healthScopeAppId: AgentType.RMM, healthWarning: true };
      case ComputersHealthFilterType.Unsupported:
        return { type: ComputersHealthFilterType.Unsupported };
      case ComputersHealthFilterType.Outdated:
        return { type: ComputersHealthFilterType.Outdated };
      case ComputersHealthFilterType.InstallIssues:
        return { installError: true };
      default:
        return {};
    }
  }

  validateSearchParams(search: SmartSearchModel): Observable<SmartSearchValidationState> {
    const areValid = (filter: SmartSearchFilterOptions) => SmartSearchUtils.areValuesByTagValid(filter.tag, search, filter.values);
    const haveValuesAndValid = (filter: SmartSearchFilterOptions) =>
      SmartSearchUtils.haveValues(filter.tag, search) ? areValid(filter) : SmartSearchValidationState.Unknown;

    return combineLatest([this.tagsFacade.isTagsFilterValid(search), this.licensesSignalFacade.isLicenseTypesFilterValid(search)]).pipe(
      map(
        (validationStates) =>
          Math.max(
            ...validationStates.concat(filters.filter((filter) => !!filter.values).map((filter) => haveValuesAndValid(filter)))
          ) as SmartSearchValidationState
      )
    );
  }

  private enrichParamsByPayload(params: { [key: string]: any }, payload: { [key: string]: any }): { [key: string]: any } {
    for (const key in payload) {
      if (!isNil(payload[key])) {
        params[key] = payload[key];
      }
    }

    return params;
  }

  private getSearchHttpParams(search: SmartSearchModel): ComputersStoreFilters {
    const params = {} as ComputersStoreFilters;

    if (!search) {
      return params;
    }

    filters.forEach((filter) => {
      const values = getSmartSearchTagValues(filter.tag, search);
      if (values.length && SmartSearchUtils.areValuesValid(values, filter.values)) {
        const formattedValues = values
          .map((value) => (filter.formatValue ? filter.formatValue(value) : value.toLowerCase()))
          .filter(Boolean);
        const separator = filter.singleResultSeparator || queryValueSeparator;
        formattedValues.length && (params[filter.key] = filter.singleResultValue ? formattedValues.join(separator) : formattedValues);
      }
    });

    return params;
  }

  private prepareTagsFilter(search: SmartSearchModel): Observable<number[]> {
    return of(getSmartSearchTagValues(SmartSearchTemplateName.Tag, search)).pipe(
      switchMap((searchTags) =>
        searchTags.length
          ? this.tagsFacade.loadAllTags().pipe(
              switchMap(() => this.tagsFacade.tagLoaded$),
              filter(Boolean),
              switchMap(() => this.tagsFacade.allTags$),
              take(1),
              map((allTags) =>
                allTags
                  .filter((tag) => searchTags.map((tagName) => tagName.toLowerCase()).includes(tag.name.toLowerCase()))
                  .map((tag) => tag.id)
              )
            )
          : of(undefined)
      )
    );
  }

  private prepareLicenseTypesFilter(search: SmartSearchModel): Observable<number[]> {
    return of(getSmartSearchTagValues(SmartSearchTemplateName.LicenseType, search)).pipe(
      switchMap((searchTags) =>
        searchTags.length
          ? this.licenseTypesObservable.pipe(
              filter((types) => types !== null),
              take(1),
              map(() => this.licensesSignalFacade.licenseTypesWithDisplayNamePrePostByOwner()),
              map((types) =>
                types
                  .filter((type) => searchTags.map((tagName) => tagName.toLowerCase()).includes(type.displayName.toLowerCase()))
                  .map((tag) => tag.type)
              )
            )
          : of(undefined)
      )
    );
  }
}
